import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TenantService } from '../../../../../Services/tenant.service';
import { ModuleService } from '../../Services/module.service';
import { ModuleWithAllVersions } from '@app/Tenant/Applications/Airsweb/Models/modules-with-all-versions.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CompareFormFlowStateVersions } from '@app/Tenant/Applications/Airsweb/Models/compare-formflow-state-versions';
import { MatFormField } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

// TO-DO look into alternative json compare libraries with more configuration (e.g. filter out non changed areas)
declare var difflib: any;

@Component({
    selector: 'aw-aw-diff',
    templateUrl: './aw-diff.component.html',
    styleUrls: ['./aw-diff.component.less'],
    imports: [RouterLink, MatFormField, MatSelect, FormsModule, MatOption, MatButton, ReactiveFormsModule, MatProgressSpinner]
})
export class AwDiffComponent implements OnInit, OnDestroy {
    @ViewChild('comparison', { static: true }) output: ElementRef;
    routeModuleId: string = '';
    baseTextSelection: string = 'snapshot';
    compareTextSelection: string = 'changes';
    loadstatus: string;
    moduleData: any = null;
    formChanges: boolean = true;
    contextSize: number = 50;
    inline: boolean = false;
    versionFrom!: number;
    versionTo!: number;
    isCompareVersionsTab = false;
    moduleUniqueId: string;
    modulesWithVersions: ModuleWithAllVersions[];
    compareForm: UntypedFormGroup;
    availableVersions: number[] = [];
    compareChangesLoading: boolean = false;

    private snapShotData: {
        snapShot?: string;
        workingVersion?: string;
        latestVersion?: string;
    } = {};
    private versionCompareResults: CompareFormFlowStateVersions;
    private isDestroyed$ = new Subject<void>();

    constructor(
      private fb: UntypedFormBuilder,
      private moduleService: ModuleService,
      private activatedRoute: ActivatedRoute,
      private tenantService: TenantService,
      private router: Router
    ) {}

    ngOnInit() {
        this.initCompareChangesTab();

        if (!this.isCompareVersionsTab) {
            this.activatedRoute.paramMap.subscribe(params => {
                this.routeModuleId = params.get('moduleId');
                let version = +params.get('version');
                if (version) {
                    this.moduleService
                        .previewChanges(this.tenantService.selectedTenant, this.routeModuleId)
                        .then(res => {
                            this.moduleData = res;
                            this.setFormCompare();
                        });
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.isDestroyed$.next();
        this.isDestroyed$.complete();

    }

    onModuleChange(): void {
        const selectedModuleId = this.compareForm.get('moduleUniqueId').value;
        const selectedModule = this.modulesWithVersions.find(m => m.ModuleId === selectedModuleId);
        this.availableVersions = selectedModule ? selectedModule.Versions : [];

        this.compareForm.patchValue({
            versionFrom: null,
            versionTo: null
        });
    }

    compareVersions(): void {
        if (this.compareForm.valid) {
            this.compareChangesLoading = true;
            const { moduleUniqueId, versionFrom, versionTo } = this.compareForm.value;
            this.moduleService.compareVersions(this.tenantService.selectedTenant, moduleUniqueId, versionFrom, versionTo)
                .pipe(takeUntil(this.isDestroyed$))
                .subscribe((result: CompareFormFlowStateVersions) => {
                    this.versionCompareResults = result;
                    this.setFormFlowVersionCompare();
                });
        }
    }

    setStateCompare() {
        if(this.isCompareVersionsTab) {
            this.setStateVersionCompare();
            this.formChanges = false;
            return;
        }

        if (this.moduleData.SnapShot) {
            this.snapShotData.snapShot = JSON.stringify(JSON.parse(this.moduleData.SnapShot.StateContent), null, "\t");
        }
        if (this.moduleData.workingVersion) {
            this.snapShotData.workingVersion = JSON.stringify(JSON.parse(this.moduleData.workingVersion.StateContent), null, "\t");
        }
        if (this.moduleData.latestVersion) {
            this.snapShotData.latestVersion = JSON.stringify(JSON.parse(this.moduleData.latestVersion.StateContent), null, "\t");
        }
        this.update();
        this.formChanges = false;
    }

    setFormCompare() {
        if(this.isCompareVersionsTab) {
            this.setFormFlowVersionCompare();
            this.formChanges = true;
            return;
        }

        if (this.moduleData.SnapShot) {
            this.snapShotData.snapShot = JSON.stringify(JSON.parse(this.moduleData.SnapShot.FormFlowContent), null, "\t");
        }
        if (this.moduleData.workingVersion) {
            this.snapShotData.workingVersion = JSON.stringify(JSON.parse(this.moduleData.workingVersion.FormFlowContent), null, "\t");
        }
        if (this.moduleData.latestVersion) {
            this.snapShotData.latestVersion = JSON.stringify(JSON.parse(this.moduleData.latestVersion.FormFlowContent), null, "\t");
        }
        this.update();
        this.formChanges = true;
    }

    getSelection(selection: string) {
        switch (selection) {
            case 'live':
                return this.snapShotData.latestVersion;
            case 'changes':
                return this.snapShotData.workingVersion;
            case 'snapshot':
                return this.snapShotData.snapShot;
        }
    }

    getSelectionText(selection: string) {
        switch (selection) {
        case 'live':
            return 'From Live';
        case 'changes':
            return 'Working Version';
        case 'snapshot':
            return 'From Version Snapshot';
        }
    }

    update(): void {
        if (!this.isCompareVersionsTab) {
            const baseText = this.getSelection(this.baseTextSelection);
            const compareText = this.getSelection(this.compareTextSelection);
            if (!(baseText && compareText)) {
                this.loadstatus = 'unableToLoad';
                return;
            }
            const result = difflib.buildView({
                baseText: baseText,
                newText: compareText,
                baseTextName: this.getSelectionText(this.baseTextSelection),
                newTextName: this.getSelectionText(this.compareTextSelection),
                contextSize: this.contextSize,
                inline: this.inline
            });
            while (this.output.nativeElement.firstChild) {
                this.output.nativeElement.firstChild.remove();
            }
            this.output.nativeElement.appendChild(result);
        }
    }

    private updateCompare(fromVersionJson, toVersionJson): void {
        if (this.isCompareVersionsTab) {
            this.compareChangesLoading = false;
            const result = difflib.buildView({
                baseText: JSON.stringify(fromVersionJson, null, "\t"),
                newText: JSON.stringify(toVersionJson, null, "\t"),
                baseTextName: 'Version From',
                newTextName: 'Version To',
                contextSize: this.contextSize,
                inline: this.inline
            });

            while (this.output.nativeElement.firstChild) {
                this.output.nativeElement.firstChild.remove();
            }

            this.output.nativeElement.append(result);
        }
    }

    private initCompareChangesTab(): void {
        this.isCompareVersionsTab = this.router.url.includes('/comparechanges');
        this.compareForm = this.fb.group({
            moduleUniqueId: [null, Validators.required],
            versionFrom: [null, Validators.required],
            versionTo: [null, Validators.required]
        });

        this.moduleService.getAllModulesWithVersions(this.tenantService.selectedTenant)
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((modules: ModuleWithAllVersions[]) => {
                this.modulesWithVersions = modules.map(module => ({
                    ...module,
                    versions: module.Versions
                }));
            });
    }

    private setStateVersionCompare(): void {
        this.updateCompare(this.versionCompareResults.StatesFrom, this.versionCompareResults.StatesTo);
    }

    private setFormFlowVersionCompare(): void {
        this.updateCompare(this.versionCompareResults.FormFlowFrom, this.versionCompareResults.FormFlowTo);
    }
}
